import { useEffect, useState } from "react";

// Normalize a backend timestamp (epoch seconds, epoch ms, or ISO string) to ms.
function toMs(datetime: number | string): number {
    if (typeof datetime === "number") {
        // Heuristic: values below ~1e12 are epoch seconds, not milliseconds.
        return datetime < 1e12 ? datetime * 1000 : datetime;
    }
    const parsed = Date.parse(datetime);
    return Number.isNaN(parsed) ? Date.now() : parsed;
}

// Hebrew relative-time label, e.g. "עכשיו", "לפני דקה", "לפני 5 דקות",
// "לפני שעה", "לפני שעתיים", "לפני שעה ו-23 דקות", "לפני יום".
export function formatRelativeHe(datetime: number | string): string {
    const diffMs = Date.now() - toMs(datetime);
    const diffSec = Math.max(0, Math.floor(diffMs / 1000));
    const minutes = Math.floor(diffSec / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "עכשיו";

    if (hours < 1) {
        return minutes === 1 ? "לפני דקה" : `לפני ${minutes} דקות`;
    }

    if (days < 1) {
        const remMin = minutes - hours * 60;
        const hourPart = hours === 1 ? "שעה" : hours === 2 ? "שעתיים" : `${hours} שעות`;
        if (remMin === 0) return `לפני ${hourPart}`;
        const minPart = remMin === 1 ? "דקה" : `${remMin} דקות`;
        return `לפני ${hourPart} ו-${minPart}`;
    }

    return days === 1 ? "לפני יום" : days === 2 ? "לפני יומיים" : `לפני ${days} ימים`;
}

// Live relative-time string that updates without a page refresh.
export function useRelativeTime(datetime: number | string): string {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 30000);
        return () => clearInterval(id);
    }, []);
    return formatRelativeHe(datetime);
}
